import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { batchAPI } from '../services/api';

const BatchRework = () => {
  const { batchId } = useParams();
  const navigate = useNavigate();

  const [failedImages, setFailedImages] = useState([]);
  const [reworkFiles, setReworkFiles] = useState({});
  const [uploading, setUploading] = useState(false);
  const [validating, setValidating] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [error, setError] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const dragCounterRef = useRef(0);

  useEffect(() => {
    fetchFailedImages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [batchId]);

  // Helper function to parse failed checks from string or array
  const parseFailedChecks = (failedChecks) => {
    if (!failedChecks) return [];

    // If it's already an array (old format)
    if (Array.isArray(failedChecks)) {
      // Check if it's an array of single characters (wrong format)
      // If so, join them back and split properly
      if (failedChecks.every(item => typeof item === 'string' && item.length === 1)) {
        const joinedString = failedChecks.join('');
        return [joinedString]; // Return as single item
      }
      return failedChecks.filter(check => check);
    }

    // If it's a string
    if (typeof failedChecks === 'string') {
      if (failedChecks === '') return [];

      // NEW FORMAT: Split by || delimiter first
      if (failedChecks.includes('||')) {
        return failedChecks.split('||').map(check => check.trim()).filter(check => check);
      }

      // OLD FORMAT: Split by comma for backward compatibility
      const checks = failedChecks.split(',').map(check => check.trim()).filter(check => check);

      // If no delimiters found, return the whole string as one check
      if (checks.length === 0 && failedChecks.trim()) {
        return [failedChecks.trim()];
      }

      return checks;
    }

    return [];
  };

  const fetchFailedImages = async () => {
    try {
      const imagesRes = await batchAPI.getImages(batchId);

      const failed = imagesRes.data.filter(img =>
        img.overall_status?.toLowerCase() === 'fail' ||
        img.validation_status?.toLowerCase() === 'fail'
      );
      setFailedImages(failed);
    } catch (error) {
      console.error('Error fetching failed images:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to load images';
      setError(typeof errorMessage === 'string' ? errorMessage : JSON.stringify(errorMessage));
    }
  };

  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    const newReworkFiles = { ...reworkFiles };

    files.forEach(file => {
      // Match file to failed image by filename
      const matchingImage = failedImages.find(img =>
        img.filename === file.name || img.original_filename === file.name
      );

      if (matchingImage) {
        newReworkFiles[matchingImage.image_id] = file;
      }
    });

    setReworkFiles(newReworkFiles);
    setError('');
  };

  const handleBulkUploadAndValidate = async () => {
    const filesToUpload = Object.values(reworkFiles);

    if (filesToUpload.length === 0) {
      setError('Please select at least one file to upload');
      return;
    }

    setUploading(true);
    setError('');

    try {
      // Step 1: Request presigned URLs for S3 upload
      setUploadProgress({ percent: 0, stage: 'Requesting upload URLs...' });

      const filePaths = Object.values(reworkFiles).map(file => file.name);

      console.log('📤 Requesting presigned URLs for rework files:', filePaths);
      const urlResponse = await batchAPI.requestReworkS3Upload(batchId, filePaths);
      const { presigned_urls, concurrent_uploads } = urlResponse.data;

      console.log(`✅ Received ${Object.keys(presigned_urls).length} presigned URLs`);

      // Step 2: Upload files directly to S3 in batches
      setUploadProgress({ percent: 5, stage: 'Uploading to S3...' });

      const filesArray = Object.values(reworkFiles);
      const totalFiles = filesArray.length;
      let uploadedCount = 0;

      // Upload in batches (concurrent_uploads at a time)
      for (let i = 0; i < filesArray.length; i += concurrent_uploads) {
        const batch = filesArray.slice(i, i + concurrent_uploads);

        const uploadResults = await Promise.all(
          batch.map(async (file) => {
            const presignedUrl = presigned_urls[file.name];
            if (!presignedUrl) {
              console.error(`No presigned URL for file: ${file.name}`);
              return { success: false, fileName: file.name };
            }

            try {
              await batchAPI.uploadToS3(presignedUrl, file, (progressEvent) => {
                // Individual file progress (optional - can be enhanced)
                const filePercent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                console.log(`Uploading ${file.name}: ${filePercent}%`);
              });

              console.log(`✅ Uploaded: ${file.name}`);
              return { success: true, fileName: file.name };
            } catch (uploadError) {
              console.error(`❌ Failed to upload ${file.name}:`, uploadError);
              throw new Error(`Failed to upload ${file.name}`);
            }
          })
        );

        // Update count after batch completes
        uploadedCount += uploadResults.filter(r => r.success).length;
        const overallPercent = 5 + Math.round((uploadedCount / totalFiles) * 60); // 5-65%
        setUploadProgress({
          percent: overallPercent,
          stage: `Uploading to S3... (${uploadedCount}/${totalFiles})`
        });
      }

      setUploadProgress({ percent: 65, stage: 'Upload complete! Confirming...' });
      console.log(`✅ All ${uploadedCount} files uploaded to S3`);

      // Step 3: Confirm upload and trigger validation
      setUploadProgress({ percent: 70, stage: 'Starting validation...' });
      console.log('🔄 Confirming S3 upload and triggering validation...');

      await batchAPI.confirmReworkS3Upload(batchId);

      console.log('✅ Validation started');
      setUploadProgress({ percent: 75, stage: 'Validating rework images...' });

      // Step 4: Wait for validation to complete
      setValidating(true);
      await pollValidationStatus();

    } catch (error) {
      console.error('Error in S3 rework upload:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to process rework images';
      setError(errorMessage);
    } finally {
      setUploading(false);
      setValidating(false);
    }
  };

  const pollValidationStatus = async () => {
    let attempts = 0;
    const maxAttempts = 60; // 60 seconds max wait (rework validation may take longer)

    return new Promise((resolve) => {
      const interval = setInterval(async () => {
        attempts++;

        try {
          const progressRes = await batchAPI.getValidationProgress(batchId);
          const { processed_images, total_images, current_status } = progressRes.data;

          // Map progress to 75-100% range (since upload was 0-75%)
          const validationPercent = 75 + Math.round((processed_images / total_images) * 25);
          setUploadProgress({
            percent: validationPercent,
            stage: 'Validating...'  // Just show "Validating..." without count
          });

          // Include 'processing_rework' as a valid in-progress status
          const completedStatuses = ['waiting', 'errors', 'approved'];
          const isComplete = completedStatuses.includes(current_status);

          if (isComplete || attempts >= maxAttempts) {
            clearInterval(interval);
            setUploadProgress({ percent: 100, stage: 'Complete!' });

            // Reset uploaded files so they don't appear again after rework completion
            setReworkFiles({});

            // Refresh the failed images list
            await fetchFailedImages();

            if (current_status === 'waiting') {
              alert('✅ All rework images passed validation! Batch is ready for approval.');
              navigate(`/batch/${batchId}`);
            } else if (current_status === 'approved') {
              alert('✅ Batch already approved!');
              navigate(`/batch/${batchId}`);
            } else {
              alert('⚠️ Some rework images still have validation errors. You can upload and try again.');
            }

            resolve();
          }
        } catch (error) {
          console.error('Error polling validation:', error);
          clearInterval(interval);
          resolve();
        }
      }, 1000); // Check every second
    });
  };

  const removeFile = (imageId) => {
    const newFiles = { ...reworkFiles };
    delete newFiles[imageId];
    setReworkFiles(newFiles);
  };

  const processDroppedFiles = (files) => {
    const newReworkFiles = { ...reworkFiles };
    Array.from(files).forEach(file => {
      const matchingImage = failedImages.find(img =>
        img.filename === file.name || img.original_filename === file.name
      );
      if (matchingImage) {
        newReworkFiles[matchingImage.image_id] = file;
      }
    });
    setReworkFiles(newReworkFiles);
    setError('');
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current++;
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current--;
    if (dragCounterRef.current === 0) {
      setIsDragging(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    dragCounterRef.current = 0;
    if (uploading || validating) return;
    const { files } = e.dataTransfer;
    if (files && files.length > 0) {
      processDroppedFiles(files);
    }
  };

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.titleSection}>
          <div style={styles.logo}>🔧</div>
          <div>
            <h1 style={styles.title}>Rework Failed Images</h1>
            <p style={styles.subtitle}>Replace failed images with corrected versions</p>
          </div>
        </div>
        <button
          onClick={() => navigate(`/batch/${batchId}`)}
          style={{
            ...styles.backButton,
            opacity: uploading || validating ? 0.4 : 1,
            cursor: uploading || validating ? 'not-allowed' : 'pointer'
          }}
          disabled={uploading || validating}
          title={uploading || validating ? 'Please wait for upload and validation to complete' : ''}
        >
          {uploading || validating ? '⏳ Processing...' : '← Back to Batch'}
        </button>
      </div>

      {/* Instructions */}
      <div style={styles.mainContent}>
        <div style={styles.instructionsCard}>
          <div style={styles.instructionIcon}>⚠️</div>
          <div>
            <h3 style={styles.instructionTitle}>Rework Instructions</h3>
            <p style={styles.instructionText}>
              Upload corrected versions of the failed images. Make sure to fix the issues listed below each image.
              After uploading, the images will be automatically re-validated. Once all images pass, they will replace
              the original failed images based on matching filenames.
            </p>
          </div>
        </div>

        {/* Bulk Upload Section */}
        <div style={styles.uploadSection}>
          <div style={styles.bulkUploadCard}>
            <h3 style={styles.uploadTitle}>📤 Upload All Rework Images</h3>
            <p style={styles.uploadSubtitle}>
              Select multiple files at once. Files will be matched to failed images by filename.
            </p>

            <div
              style={{
                ...styles.uploadArea,
                ...(isDragging ? styles.uploadAreaDragging : {})
              }}
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
            >
              <input
                type="file"
                multiple
                accept="image/*,.tif,.tiff"
                onChange={handleFileSelect}
                style={styles.fileInput}
                id="bulk-upload"
                disabled={uploading || validating}
              />
              <label htmlFor="bulk-upload" style={{
                ...styles.uploadLabel,
                ...(isDragging ? styles.uploadLabelDragging : {})
              }}>
                <div style={styles.uploadIcon}>{isDragging ? '📂' : '📁'}</div>
                <div style={styles.uploadText}>
                  {isDragging ? 'Drop files here' : 'Click to select multiple files'}
                </div>
                <div style={styles.uploadHint}>
                  or drag and drop here
                </div>
              </label>
            </div>

            {Object.keys(reworkFiles).length > 0 && (
              <div style={styles.selectedFilesSection}>
                <h4 style={styles.selectedTitle}>
                  Selected Files ({Object.keys(reworkFiles).length})
                </h4>
                <div style={styles.filesList}>
                  {Object.entries(reworkFiles).map(([imageId, file]) => (
                      <div key={imageId} style={styles.fileItem}>
                        <div style={styles.fileInfo}>
                          <span style={styles.fileName}>✓ {file.name}</span>
                          <span style={styles.fileSize}>
                            ({(file.size / 1024 / 1024).toFixed(2)} MB)
                          </span>
                        </div>
                        <button
                          onClick={() => removeFile(imageId)}
                          style={styles.removeButton}
                          disabled={uploading || validating}
                        >
                          ✕
                        </button>
                      </div>
                  ))}
                </div>
              </div>
            )}

            {error && (
              <div style={styles.errorBox}>
                ❌ {error}
              </div>
            )}

            {(uploadProgress.stage || uploading || validating) && (
              <div style={styles.progressSection}>
                <div style={styles.progressBar}>
                  <div
                    style={{
                      ...styles.progressFill,
                      width: `${uploadProgress.percent || 0}%`
                    }}
                  />
                </div>
                <div style={styles.progressText}>
                  {uploadProgress.stage || 'Processing...'}
                </div>
              </div>
            )}

            <button
              onClick={handleBulkUploadAndValidate}
              disabled={Object.keys(reworkFiles).length === 0 || uploading || validating}
              style={{
                ...styles.submitButton,
                opacity: Object.keys(reworkFiles).length === 0 || uploading || validating ? 0.5 : 1,
                cursor: Object.keys(reworkFiles).length === 0 || uploading || validating ? 'not-allowed' : 'pointer'
              }}
            >
              {uploading ? '⏳ Uploading...' : validating ? '🔄 Validating...' : `🚀 Upload & Validate All (${Object.keys(reworkFiles).length})`}
            </button>
          </div>
        </div>

        {/* Failed Images List */}
        <div style={styles.failedImagesSection}>
          <h2 style={styles.sectionTitle}>
            Failed Images ({failedImages.length})
          </h2>

          {failedImages.map((image, index) => (
            <div key={image.image_id} style={styles.imageCard}>
              <div style={styles.imageHeader}>
                <div style={styles.imageTitle}>
                  <span style={styles.imageNumber}>
                    IMG-{String(image.sequence_number).padStart(4, '0')}
                  </span>
                  <span style={styles.imageName}>{image.filename}</span>
                </div>
                <span style={styles.failedBadge}>FAILED</span>
              </div>

              <div style={styles.failedChecksSection}>
                <div style={styles.failedChecksTitle}>❌ Failed Validation Checks:</div>
                <div style={styles.failedChecksList}>
                  {(() => {
                    const checks = parseFailedChecks(image.failed_checks);
                    if (checks.length === 0) {
                      return <span style={styles.noChecks}>No specific checks listed</span>;
                    }
                    // Display as bullet points
                    return (
                      <ul style={{ margin: 0, paddingLeft: '20px', textAlign: 'left' }}>
                        {checks.map((check, idx) => (
                          <li key={idx} style={styles.checkBadge}>
                            {check.replace(/_/g, ' ')}
                          </li>
                        ))}
                      </ul>
                    );
                  })()}
                </div>
              </div>

              <div style={styles.uploadStatus}>
                {reworkFiles[image.image_id] ? (
                  <div style={styles.fileSelected}>
                    ✓ Rework file selected: {reworkFiles[image.image_id].name}
                  </div>
                ) : (
                  <div style={styles.fileNotSelected}>
                    ⚠️ No rework file selected for this image
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const styles = {
  container: {
    minHeight: '100vh',
    backgroundColor: '#f8fafc',
  },
  header: {
    backgroundColor: 'white',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    borderBottom: '1px solid #e5e7eb',
    padding: '20px 30px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  titleSection: {
    display: 'flex',
    alignItems: 'center',
    gap: '15px',
  },
  logo: {
    fontSize: '32px',
  },
  title: {
    fontSize: '24px',
    fontWeight: 'bold',
    color: '#1f2937',
    margin: 0,
  },
  subtitle: {
    fontSize: '14px',
    color: '#6b7280',
    margin: '2px 0 0 0',
  },
  backButton: {
    padding: '10px 20px',
    backgroundColor: '#6b7280',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  mainContent: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '30px',
  },
  instructionsCard: {
    backgroundColor: '#fef3c7',
    border: '1px solid #fde047',
    borderRadius: '12px',
    padding: '20px',
    marginBottom: '30px',
    display: 'flex',
    gap: '16px',
  },
  instructionIcon: {
    fontSize: '24px',
  },
  instructionTitle: {
    fontSize: '16px',
    fontWeight: 'bold',
    color: '#92400e',
    marginBottom: '8px',
  },
  instructionText: {
    fontSize: '14px',
    color: '#78350f',
    lineHeight: '1.6',
  },
  uploadSection: {
    marginBottom: '30px',
  },
  bulkUploadCard: {
    backgroundColor: 'white',
    borderRadius: '12px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    border: '1px solid #e5e7eb',
    padding: '30px',
  },
  uploadTitle: {
    fontSize: '20px',
    fontWeight: 'bold',
    color: '#1f2937',
    marginBottom: '8px',
  },
  uploadSubtitle: {
    fontSize: '14px',
    color: '#6b7280',
    marginBottom: '24px',
  },
  uploadArea: {
    position: 'relative',
    marginBottom: '20px',
  },
  uploadAreaDragging: {
    borderRadius: '12px',
  },
  fileInput: {
    display: 'none',
  },
  uploadLabel: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '60px 20px',
    border: '2px dashed #d1d5db',
    borderRadius: '12px',
    backgroundColor: '#f9fafb',
    cursor: 'pointer',
    transition: 'all 0.3s',
  },
  uploadLabelDragging: {
    border: '2px dashed #3b82f6',
    backgroundColor: '#eff6ff',
  },
  uploadIcon: {
    fontSize: '48px',
    marginBottom: '16px',
  },
  uploadText: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#374151',
    marginBottom: '8px',
  },
  uploadHint: {
    fontSize: '14px',
    color: '#6b7280',
  },
  selectedFilesSection: {
    marginBottom: '20px',
  },
  selectedTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#1f2937',
    marginBottom: '12px',
  },
  filesList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  fileItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 16px',
    backgroundColor: '#f0fdf4',
    border: '1px solid #bbf7d0',
    borderRadius: '8px',
  },
  fileInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  fileName: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#166534',
  },
  fileSize: {
    fontSize: '12px',
    color: '#65a30d',
  },
  removeButton: {
    padding: '4px 12px',
    backgroundColor: '#fee2e2',
    color: '#991b1b',
    border: 'none',
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  errorBox: {
    backgroundColor: '#fee2e2',
    border: '1px solid #fecaca',
    borderRadius: '8px',
    padding: '12px 16px',
    color: '#dc2626',
    fontSize: '14px',
    marginBottom: '16px',
  },
  progressSection: {
    marginBottom: '20px',
  },
  progressBar: {
    width: '100%',
    height: '8px',
    backgroundColor: '#e5e7eb',
    borderRadius: '4px',
    overflow: 'hidden',
    marginBottom: '8px',
  },
  progressFill: {
    height: '100%',
    background: 'linear-gradient(90deg, #3b82f6, #2563eb)',
    transition: 'width 0.3s ease',
  },
  progressText: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#3b82f6',
    textAlign: 'center',
  },
  submitButton: {
    width: '100%',
    padding: '16px',
    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  failedImagesSection: {
    marginTop: '30px',
  },
  sectionTitle: {
    fontSize: '20px',
    fontWeight: 'bold',
    color: '#1f2937',
    marginBottom: '20px',
  },
  imageCard: {
    backgroundColor: 'white',
    borderRadius: '12px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    border: '1px solid #e5e7eb',
    padding: '20px',
    marginBottom: '16px',
  },
  imageHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px',
  },
  imageTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  imageNumber: {
    fontFamily: 'monospace',
    fontWeight: '600',
    color: '#3b82f6',
    fontSize: '16px',
  },
  imageName: {
    fontFamily: 'monospace',
    fontSize: '14px',
    color: '#6b7280',
  },
  failedBadge: {
    padding: '4px 12px',
    backgroundColor: '#fee2e2',
    color: '#dc2626',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: '600',
  },
  failedChecksSection: {
    marginBottom: '16px',
  },
  failedChecksTitle: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#dc2626',
    marginBottom: '8px',
  },
  failedChecksList: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
  },
  checkBadge: {
    padding: '4px 12px',
    backgroundColor: '#fef2f2',
    color: '#991b1b',
    borderRadius: '6px',
    fontSize: '12px',
    fontWeight: '500',
  },
  noChecks: {
    fontSize: '14px',
    color: '#6b7280',
    fontStyle: 'italic',
  },
  uploadStatus: {
    paddingTop: '12px',
    borderTop: '1px solid #e5e7eb',
  },
  fileSelected: {
    fontSize: '14px',
    color: '#059669',
    fontWeight: '600',
  },
  fileNotSelected: {
    fontSize: '14px',
    color: '#d97706',
    fontWeight: '600',
  },
};

export default BatchRework;